import { useSharedAPI } from "@certn/api";
import { BasicButton, Link, Stack, Text } from "@certn/cascadia";
import { PrimaryButton } from "@certn/cascadia/components/button/PrimaryButton";
import {
  SelectFormikField,
  SelectItem,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ManualIdvFileUpload } from "./ManualIdvFileUpload";

const DBS_DOCUMENT_LISTS_URL =
  "https://www.gov.uk/government/publications/dbs-identity-checking-guidelines/id-checking-guidelines-for-standardenhanced-dbs-check-applications-from-22-april-2025#document-lists";

export interface DocumentEntry {
  document_group: string;
  document_type: string;
  file_key: string;
  file_name: string;
  saved?: boolean;
  passport_details?: Record<string, string>;
  uk_driving_licence_details?: Record<string, string>;
  download_url?: string;
}

const DOCUMENT_GROUPS = [
  { key: "GROUP_1", label: "Group 1 — Primary identity documents" },
  { key: "GROUP_2A", label: "Group 2a — Trusted government documents" },
  {
    key: "GROUP_2B",
    label: "Group 2b — Financial and social history documents",
  },
];

const DOCUMENT_TYPES_BY_GROUP: Record<
  string,
  { key: string; label: string }[]
> = {
  GROUP_1: [
    { key: "ADOPTION_CERTIFICATE", label: "Adoption certificate (UK)" },
    {
      key: "APPLICATION_REGISTRATION_CARD",
      label: "Application Registration Card (ARC)",
    },
    {
      key: "BIOMETRIC_RESIDENCE_PERMIT",
      label: "Biometric residence permit (BRP)",
    },
    {
      key: "BIRTH_CERTIFICATE_ISSUED_WITHIN_12_MONTHS",
      label: "Birth certificate (issued within 12 months of birth)",
    },
    {
      key: "CURRENT_DRIVING_LICENCE_PHOTOCARD",
      label: "Driving licence photocard (UK, Isle of Man or Channel Islands)",
    },
    { key: "EVISA", label: "eVisa (UK immigration status)" },
    { key: "PASSPORT", label: "Passport (any nationality)" },
  ],
  GROUP_2A: [
    {
      key: "BIRTH_CERTIFICATE_AFTER_12_MONTHS",
      label: "Birth certificate (issued more than 12 months after birth)",
    },
    {
      key: "CURRENT_DRIVING_LICENCE_PHOTOCARD_NON_UK",
      label: "Driving licence photocard (non-UK)",
    },
    {
      key: "CURRENT_DRIVING_LICENCE_PAPER",
      label: "Driving licence paper licence (UK – issued before March 2000)",
    },
    { key: "FIREARMS_LICENCE", label: "Firearms licence" },
    {
      key: "HM_FORCES_ID_CARD",
      label: "HM Forces identity card / Armed Forces Veteran card",
    },
    {
      key: "IMMIGRATION_DOCUMENT_VISA_WORK_PERMIT",
      label: "Immigration document, visa or work permit",
    },
    {
      key: "MARRIAGE_CIVIL_PARTNERSHIP_CERTIFICATE",
      label: "Marriage or civil partnership certificate",
    },
  ],
  GROUP_2B: [
    {
      key: "BANK_BUILDING_SOCIETY_ACCOUNT_OPENING_LETTER",
      label: "Bank or building society account opening confirmation letter",
    },
    {
      key: "BANK_BUILDING_SOCIETY_STATEMENT",
      label: "Bank or building society statement",
    },
    { key: "BENEFIT_STATEMENT", label: "Benefit statement" },
    {
      key: "CENTRAL_LOCAL_GOVERNMENT_LETTER",
      label: "Central or local government letter",
    },
    { key: "COUNCIL_TAX_STATEMENT", label: "Council tax statement" },
    { key: "CREDIT_CARD_STATEMENT", label: "Credit card statement" },
    {
      key: "EHIC_GHIC",
      label:
        "European Health Insurance Card (EHIC) / Global Health Insurance Card (GHIC)",
    },
    {
      key: "FINANCIAL_STATEMENT",
      label: "Financial statement (pension/endowment)",
    },
    {
      key: "HMRC_DOCUMENT",
      label: "HMRC self-assessment or tax demand letter",
    },
    {
      key: "LETTER_FROM_HEAD_TEACHER",
      label:
        "Letter from head teacher / college principal / apprenticeship provider",
    },
    {
      key: "LETTER_OF_SPONSORSHIP",
      label: "Letter of sponsorship from future UK employer",
    },
    { key: "MORTGAGE_STATEMENT", label: "Mortgage statement" },
    { key: "P45_STATEMENT", label: "P45 statement" },
    { key: "P60_STATEMENT", label: "P60 statement" },
    { key: "PASS_CARD", label: "PASS card (proof-of-age card)" },
    { key: "UTILITY_BILL", label: "Utility bill" },
  ],
};

function getGroupLabel(groupKey: string): string {
  return DOCUMENT_GROUPS.find((g) => g.key === groupKey)?.label ?? groupKey;
}

function getTypeLabel(groupKey: string, typeKey: string): string {
  const types = DOCUMENT_TYPES_BY_GROUP[groupKey] ?? [];
  return types.find((dt) => dt.key === typeKey)?.label ?? typeKey;
}

function useTranslateDocLabel() {
  const { t } = useTranslation();
  return useMemo(() => {
    const m: Record<string, string> = {
      "Group 1 — Primary identity documents": t(
        "Group 1 — Primary identity documents"
      ),
      "Group 2a — Trusted government documents": t(
        "Group 2a — Trusted government documents"
      ),
      "Group 2b — Financial and social history documents": t(
        "Group 2b — Financial and social history documents"
      ),
      "Adoption certificate (UK)": t("Adoption certificate (UK)"),
      "Application Registration Card (ARC)": t(
        "Application Registration Card (ARC)"
      ),
      "Biometric residence permit (BRP)": t("Biometric residence permit (BRP)"),
      "Birth certificate (issued within 12 months of birth)": t(
        "Birth certificate (issued within 12 months of birth)"
      ),
      "Driving licence photocard (UK, Isle of Man or Channel Islands)": t(
        "Driving licence photocard (UK, Isle of Man or Channel Islands)"
      ),
      "eVisa (UK immigration status)": t("eVisa (UK immigration status)"),
      "Passport (any nationality)": t("Passport (any nationality)"),
      "Birth certificate (issued more than 12 months after birth)": t(
        "Birth certificate (issued more than 12 months after birth)"
      ),
      "Driving licence photocard (non-UK)": t(
        "Driving licence photocard (non-UK)"
      ),
      "Driving licence paper licence (UK – issued before March 2000)": t(
        "Driving licence paper licence (UK – issued before March 2000)"
      ),
      "Firearms licence": t("Firearms licence"),
      "HM Forces identity card / Armed Forces Veteran card": t(
        "HM Forces identity card / Armed Forces Veteran card"
      ),
      "Immigration document, visa or work permit": t(
        "Immigration document, visa or work permit"
      ),
      "Marriage or civil partnership certificate": t(
        "Marriage or civil partnership certificate"
      ),
      "Bank or building society account opening confirmation letter": t(
        "Bank or building society account opening confirmation letter"
      ),
      "Bank or building society statement": t(
        "Bank or building society statement"
      ),
      "Benefit statement": t("Benefit statement"),
      "Central or local government letter": t(
        "Central or local government letter"
      ),
      "Council tax statement": t("Council tax statement"),
      "Credit card statement": t("Credit card statement"),
      "European Health Insurance Card (EHIC) / Global Health Insurance Card (GHIC)":
        t(
          "European Health Insurance Card (EHIC) / Global Health Insurance Card (GHIC)"
        ),
      "Financial statement (pension/endowment)": t(
        "Financial statement (pension/endowment)"
      ),
      "HMRC self-assessment or tax demand letter": t(
        "HMRC self-assessment or tax demand letter"
      ),
      "Letter from head teacher / college principal / apprenticeship provider":
        t(
          "Letter from head teacher / college principal / apprenticeship provider"
        ),
      "Letter of sponsorship from future UK employer": t(
        "Letter of sponsorship from future UK employer"
      ),
      "Mortgage statement": t("Mortgage statement"),
      "P45 statement": t("P45 statement"),
      "P60 statement": t("P60 statement"),
      "PASS card (proof-of-age card)": t("PASS card (proof-of-age card)"),
      "Utility bill": t("Utility bill"),
    };
    return (label: string) => m[label] ?? label;
  }, [t]);
}

export function SavedDocumentCard({
  index,
  onEdit,
  isReadOnly,
  downloadUrl,
}: {
  index: number;
  onEdit: () => void;
  isReadOnly?: boolean;
  downloadUrl?: string;
}) {
  const { t } = useTranslation();
  const tl = useTranslateDocLabel();
  const { values } = useFormikContext<{ documents: DocumentEntry[] }>();
  const doc = values.documents[index];

  return (
    <div className={styles.savedCard}>
      <div className={styles.savedCardContent}>
        <Text style="global/body-primary">
          {tl(getTypeLabel(doc.document_group, doc.document_type))}
        </Text>
        <Text style="global/body-secondary" color="text-weak">
          {tl(getGroupLabel(doc.document_group))}
        </Text>
        {isReadOnly && doc.file_name && (
          <Text style="global/body-secondary" color="text-weak">
            {doc.file_name}
          </Text>
        )}
      </div>
      {isReadOnly && downloadUrl ? (
        <button
          type="button"
          className={styles.editLink}
          onClick={() => window.open(downloadUrl, "_blank")}
          data-testid={`view-document-${index}`}
        >
          {t("View")}
        </button>
      ) : !isReadOnly ? (
        <button
          type="button"
          className={styles.editLink}
          onClick={onEdit}
          data-testid={`edit-document-${index}`}
        >
          {t("Edit")}
        </button>
      ) : null}
    </div>
  );
}

interface DocumentEntryFormProps {
  index: number;
  caseId?: string;
  isDisabled: boolean;
  onRemove: () => void;
  onSave: () => void;
}

export function DocumentEntryForm({
  index,
  caseId,
  isDisabled,
  onRemove,
  onSave,
}: DocumentEntryFormProps) {
  const { t } = useTranslation();
  const tl = useTranslateDocLabel();
  const { values, setFieldValue } = useFormikContext<{
    documents: DocumentEntry[];
  }>();
  const doc = values.documents[index];
  const prefix = `documents.${index}`;
  const availableTypes = doc?.document_group
    ? DOCUMENT_TYPES_BY_GROUP[doc.document_group] || []
    : [];

  const showPassportDetails = doc?.document_type === "PASSPORT";
  const showDrivingLicenceDetails =
    doc?.document_type === "CURRENT_DRIVING_LICENCE_PHOTOCARD";

  return (
    <Stack gap="space-4">
      <SelectFormikField
        testID={`${prefix}.document_group`}
        name={`${prefix}.document_group`}
        label={t("Document group")}
        isRequired
        isDisabled={isDisabled}
        onSelectionChange={() => {
          setFieldValue(`${prefix}.document_type`, "");
        }}
      >
        {DOCUMENT_GROUPS.map((group) => (
          <SelectItem key={group.key} id={group.key}>
            {tl(group.label)}
          </SelectItem>
        ))}
      </SelectFormikField>

      <SelectFormikField
        testID={`${prefix}.document_type`}
        name={`${prefix}.document_type`}
        label={t("Document type")}
        isRequired
        isDisabled={isDisabled || !doc?.document_group}
        info={
          <Link
            linkStyle="monochrome"
            textStyle="global/body-secondary-link"
            textColor="text-weak"
            href={DBS_DOCUMENT_LISTS_URL}
            testID={`${prefix}-document-type-learn-more`}
            isNewTab
          >
            {t("Learn more")}
          </Link>
        }
      >
        {availableTypes.map((type) => (
          <SelectItem key={type.key} id={type.key}>
            {tl(type.label)}
          </SelectItem>
        ))}
      </SelectFormikField>

      {showPassportDetails && (
        <PassportDetailsFields prefix={prefix} isDisabled={isDisabled} />
      )}

      {showDrivingLicenceDetails && (
        <DrivingLicenceDetailsFields prefix={prefix} isDisabled={isDisabled} />
      )}

      <ManualIdvFileUpload
        caseId={caseId}
        fieldPrefix={prefix}
        isDisabled={isDisabled}
      />

      {!isDisabled && (
        <Stack gap="space-3" orientation="horizontal">
          <PrimaryButton
            testID={`save-document-${index}`}
            type="button"
            onPress={onSave}
            isDisabled={
              !doc?.document_group || !doc?.document_type || !doc?.file_key
            }
          >
            {t("Save document")}
          </PrimaryButton>
          <BasicButton testID={`delete-document-${index}`} onPress={onRemove}>
            {t("Delete")}
          </BasicButton>
        </Stack>
      )}
    </Stack>
  );
}

function useCountryList() {
  const { data: countryList } = useSharedAPI("/api/internal/shared/countries/");
  return useMemo(
    () => countryList?.sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [countryList]
  );
}

const NATIONALITY_LABEL_OVERRIDES: Record<string, string> = {
  GB: "United Kingdom (British)",
};

function PassportDetailsFields({
  prefix,
  isDisabled,
}: {
  prefix: string;
  isDisabled: boolean;
}) {
  const { t } = useTranslation();
  const countries = useCountryList();

  return (
    <Stack gap="space-3">
      <TextInputFormikField
        testID={`${prefix}.passport_details.document_number`}
        name={`${prefix}.passport_details.document_number`}
        label={t("Passport number")}
        isRequired
        isDisabled={isDisabled}
      />
      <SelectFormikField
        testID={`${prefix}.passport_details.nationality`}
        name={`${prefix}.passport_details.nationality`}
        label={t("Nationality")}
        isDisabled={isDisabled || !countries.length}
      >
        {countries.map((c) => (
          <SelectItem key={c.alpha_2} id={c.alpha_2}>
            {NATIONALITY_LABEL_OVERRIDES[c.alpha_2] ?? c.name}
          </SelectItem>
        ))}
      </SelectFormikField>
      <Stack gap="space-6" orientation="horizontal">
        <DateFieldGroup
          prefix={`${prefix}.passport_details.document_issue_date`}
          label={t("Date of issue")}
          isDisabled={isDisabled}
        />
        <DateFieldGroup
          prefix={`${prefix}.passport_details.document_expiry_date`}
          label={t("Date of expiry")}
          isDisabled={isDisabled}
        />
      </Stack>
      <SelectFormikField
        testID={`${prefix}.passport_details.issuing_country`}
        name={`${prefix}.passport_details.issuing_country`}
        label={t("Issuing country")}
        isDisabled={isDisabled || !countries.length}
      >
        {countries.map((c) => (
          <SelectItem key={c.alpha_2} id={c.alpha_2}>
            {c.name}
          </SelectItem>
        ))}
      </SelectFormikField>
    </Stack>
  );
}

function DrivingLicenceDetailsFields({
  prefix,
  isDisabled,
}: {
  prefix: string;
  isDisabled: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Stack gap="space-3">
      <TextInputFormikField
        testID={`${prefix}.uk_driving_licence_details.number`}
        name={`${prefix}.uk_driving_licence_details.number`}
        label={t("Licence number")}
        isRequired
        isDisabled={isDisabled}
      />
      <Stack gap="space-6" orientation="horizontal">
        <DateFieldGroup
          prefix={`${prefix}.uk_driving_licence_details.date_of_birth`}
          label={t("Date of birth")}
          isDisabled={isDisabled}
        />
        <DateFieldGroup
          prefix={`${prefix}.uk_driving_licence_details.document_issue_date`}
          label={t("Date of issue")}
          isDisabled={isDisabled}
        />
      </Stack>
    </Stack>
  );
}

function DateFieldGroup({
  prefix,
  label,
  isDisabled,
}: {
  prefix: string;
  label: string;
  isDisabled: boolean;
}) {
  const { setFieldValue, getFieldMeta } = useFormikContext();

  return (
    <div>
      <Text style="input/label">{label}</Text>
      <div className={styles.dateRow}>
        <div className={styles.dateField}>
          <Text style="input/helper" color="text-weak">
            Day
          </Text>
          <input
            data-testid={`${prefix}_day`}
            placeholder="DD"
            disabled={isDisabled}
            className={styles.dateInput}
            inputMode="numeric"
            maxLength={2}
            value={String(getFieldMeta(`${prefix}_day`).value || "")}
            onChange={(e) =>
              setFieldValue(`${prefix}_day`, e.target.value.replace(/\D/g, ""))
            }
          />
        </div>
        <div className={styles.dateField}>
          <Text style="input/helper" color="text-weak">
            Month
          </Text>
          <input
            data-testid={`${prefix}_month`}
            placeholder="MM"
            disabled={isDisabled}
            className={styles.dateInput}
            inputMode="numeric"
            maxLength={2}
            value={String(getFieldMeta(`${prefix}_month`).value || "")}
            onChange={(e) =>
              setFieldValue(
                `${prefix}_month`,
                e.target.value.replace(/\D/g, "")
              )
            }
          />
        </div>
        <div className={styles.dateFieldWide}>
          <Text style="input/helper" color="text-weak">
            Year
          </Text>
          <input
            data-testid={`${prefix}_year`}
            placeholder="YYYY"
            disabled={isDisabled}
            className={styles.dateInput}
            inputMode="numeric"
            maxLength={4}
            value={String(getFieldMeta(`${prefix}_year`).value || "")}
            onChange={(e) =>
              setFieldValue(`${prefix}_year`, e.target.value.replace(/\D/g, ""))
            }
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  savedCard: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid var(--color-border-default, #e0e0e0)",
    borderRadius: "var(--border-radius-2, 8px)",
    padding: "var(--space-4)",
  }),
  savedCardContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  editLink: css({
    background: "none",
    border: "none",
    color: "var(--color-text-weak, #666)",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "inherit",
    padding: 0,
    textDecoration: "underline",
    flexShrink: 0,
    "&:hover": {
      color: "var(--color-text-base, #333)",
    },
  }),
  dateRow: css({
    display: "flex",
    gap: "var(--space-2)",
    marginTop: "var(--space-1)",
  }),
  dateField: css({
    width: "60px",
  }),
  dateFieldWide: css({
    width: "80px",
  }),
  dateInput: css({
    width: "100%",
    padding: "var(--space-2) var(--space-3)",
    border: "1px solid var(--color-border-default, #ccc)",
    borderRadius: "var(--border-radius-1, 4px)",
    fontSize: "14px",
    fontFamily: "inherit",
    marginTop: "var(--space-1)",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      borderColor: "var(--color-action-primary-default, #000)",
      boxShadow: "0 0 0 1px var(--color-action-primary-default, #000)",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "default",
    },
    "&::placeholder": {
      color: "var(--color-text-disabled, #999)",
    },
  }),
};
